import React, { useEffect, useMemo, useState } from 'react';
import { fetchSharedFinanceEvents, fetchSharedFinances } from '../data/api';
import type { SharedFinanceEventOption } from '../data/api';

// Cost line items for a document. Each line is one charge off the invoice:
// what kind of event it was for, a description, the ex-VAT amount, and the
// events it covered. The server splits the amount equally across every
// animal on those events, so a line reaching three deals' loads charges each
// deal by its head. The document form owns the file and the date; this owns
// the lines.

export interface CostLine {
  id: string;
  eventKind: EventKind;
  description: string;
  amount: string;
  eventIds: string[];
  /** The picked events' details, kept so the summary survives a filter change. */
  events: Record<string, SharedFinanceEventOption>;
}

export type EventKind = 'VACCINATION' | 'MOVEMENT' | 'SHEARING' | 'WEIGH' | 'SLAUGHTER' | 'REGISTRATION' | 'ALL';
const EVENT_KINDS: { value: EventKind; label: string; codes?: string[] }[] = [
  { value: 'VACCINATION', label: 'Vaccination / medicine', codes: ['VACCINATION', 'FEED_VAC'] },
  { value: 'MOVEMENT', label: 'Transport (receive / dispatch)', codes: ['FARM_REC', 'FEED_REC', 'ABAT_REC', 'FARM_DIS', 'FEED_DIS', 'ABAT_DIS'] },
  { value: 'SHEARING', label: 'Shearing / wool', codes: ['FARM_SHE', 'FEED_SHE', 'FEED_WOO'] },
  { value: 'WEIGH', label: 'Weighing', codes: ['FARM_WEI', 'FEED_WEI'] },
  { value: 'SLAUGHTER', label: 'Slaughter', codes: ['ABAT_SLA'] },
  { value: 'REGISTRATION', label: 'Registration', codes: ['FARM_REG', 'FEED_REG', 'ABAT_REG'] },
  { value: 'ALL', label: 'General (any event)' },
];
const EVENT_CODE_LABELS: Record<string, string> = {
  FARM_REC: 'Farm receive', FEED_REC: 'Feedlot receive', ABAT_REC: 'Abattoir receive',
  FARM_DIS: 'Farm dispatch', FEED_DIS: 'Feedlot dispatch', ABAT_DIS: 'Abattoir dispatch',
  FARM_REG: 'Farm registration', FEED_REG: 'Feedlot registration', ABAT_REG: 'Abattoir registration',
  FARM_SHE: 'Farm shearing', FEED_SHE: 'Feedlot shearing', FEED_WOO: 'Wool batch',
  FARM_WEI: 'Farm weigh', FEED_WEI: 'Feedlot weigh', ABAT_SLA: 'Slaughter',
  FEED_VAC: 'Feedlot vaccination', VACCINATION: 'Vaccination',
  FARM_MOR: 'Farm mortality', FEED_MOR: 'Feedlot mortality', ABAT_MOR: 'Abattoir mortality',
};
const codeLabel = (code: string) => EVENT_CODE_LABELS[code] || code;
const fmtR = (v: number) => `R ${v.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const newCostLine = (eventKind: EventKind = 'VACCINATION'): CostLine => ({
  id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
  eventKind, description: '', amount: '', eventIds: [], events: {},
});
// A line needs a description and a cost. Events are optional: with none, the
// cost is divided equally across every animal in the selected deals.
export const costLineIsComplete = (line: CostLine) =>
  Boolean(line.description.trim()) && Number(line.amount) > 0;
export const costLinesTotal = (lines: CostLine[]) => lines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);

/** Split an amount by head across deals, to the cent, remainder on the largest. */
export function splitByHead(amount: number, heads: { key: string; animals: number }[]): { key: string; animals: number; amount: number }[] {
  const total = heads.reduce((s, h) => s + h.animals, 0);
  if (total <= 0 || heads.length === 0) return heads.map((h) => ({ ...h, amount: 0 }));
  const parts = heads.map((h) => Math.round((amount * h.animals / total) * 100) / 100);
  const remainder = Math.round((amount - parts.reduce((s, v) => s + v, 0)) * 100) / 100;
  const largest = heads.reduce((best, h, i) => (h.animals > heads[best].animals ? i : best), 0);
  parts[largest] = Math.round((parts[largest] + remainder) * 100) / 100;
  return heads.map((h, i) => ({ ...h, amount: parts[i] }));
}

export interface DealHead { dealId: string; dealNo: string; animals: number }

/** Per-deal split of one line: by head across its events, or — with no events — across every animal in the selected deals. */
export function costLineSplit(line: CostLine, dealHeads: DealHead[]): { dealNo: string; animals: number; amount: number }[] {
  const amount = Number(line.amount) || 0;
  if (line.eventIds.length === 0) {
    return splitByHead(amount, dealHeads.map((d) => ({ key: d.dealNo, animals: d.animals }))).map((r) => ({ dealNo: r.key, animals: r.animals, amount: r.amount }));
  }
  const picked = line.eventIds.map((id) => line.events[id]).filter(Boolean);
  const byDeal = new Map<string, number>();
  picked.forEach((ev) => byDeal.set(ev.dealNo || ev.dealId || '?', (byDeal.get(ev.dealNo || ev.dealId || '?') || 0) + ev.animalCount));
  return splitByHead(amount, Array.from(byDeal.entries()).map(([key, animals]) => ({ key, animals }))).map((r) => ({ dealNo: r.key, animals: r.animals, amount: r.amount }));
}

function LineEventPicker({ line, dealIds, costedEventIds, onChange }: { line: CostLine; dealIds: string[]; costedEventIds: Set<string>; onChange: (patch: Partial<CostLine>) => void }) {
  const [search, setSearch] = useState('');
  const [events, setEvents] = useState<SharedFinanceEventOption[]>([]);
  const [loading, setLoading] = useState(false);
  const kind = EVENT_KINDS.find((k) => k.value === line.eventKind);
  const dealKey = dealIds.join(',');
  // Events of the selected deals only, and only those no cost has been
  // allocated to yet -- a costed event is spent.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all(dealIds.map((id) => fetchSharedFinanceEvents({ dealId: id, codes: kind?.codes }).catch(() => [] as SharedFinanceEventOption[])))
      .then((groups) => { if (!cancelled) setEvents(groups.flat().filter((ev) => !costedEventIds.has(ev.id))); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dealKey, line.eventKind, costedEventIds]); // eslint-disable-line
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (needle ? events.filter((ev) => [ev.dealNo, ev.dealName, codeLabel(ev.code), ev.entityName, ev.eventDate].some((v) => String(v || '').toLowerCase().includes(needle))) : events).slice(0, 40);
  }, [events, search]);
  const toggle = (ev: SharedFinanceEventOption) => {
    const eventIds = line.eventIds.includes(ev.id) ? line.eventIds.filter((x) => x !== ev.id) : [...line.eventIds, ev.id];
    onChange({ eventIds, events: { ...line.events, [ev.id]: ev } });
  };
  const allVisibleSelected = visible.length > 0 && visible.every((ev) => line.eventIds.includes(ev.id));
  const toggleAll = () => {
    if (allVisibleSelected) {
      const drop = new Set(visible.map((ev) => ev.id));
      onChange({ eventIds: line.eventIds.filter((id) => !drop.has(id)) });
    } else {
      const events = { ...line.events };
      visible.forEach((ev) => { events[ev.id] = ev; });
      onChange({ eventIds: Array.from(new Set([...line.eventIds, ...visible.map((ev) => ev.id)])), events });
    }
  };
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <label className="flex items-center gap-1.5 font-semibold text-avcs-text">
          <input type="checkbox" checked={allVisibleSelected} disabled={visible.length === 0} onChange={toggleAll} />
          Select all{visible.length > 0 ? ` (${visible.length})` : ''}
        </label>
        <input className="min-w-[10rem] flex-1 rounded-lg border border-[#1e6bb833] px-2 py-1.5" placeholder="Filter by deal, entity, date…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      {loading && <span className="text-xs text-avcs-muted">Loading events…</span>}
      {!loading && visible.length === 0 && <span className="text-xs text-avcs-muted">No uncosted {kind?.label.toLowerCase()} events on the selected deals. Leave the line without events and its cost is divided across every animal in those deals.</span>}
      {visible.length > 0 && (
        <div className="grid max-h-56 gap-1 overflow-y-auto rounded-lg border border-[#1e6bb833] bg-white p-2 sm:grid-cols-2">
          {visible.map((ev) => {
            const checked = line.eventIds.includes(ev.id);
            return (
              <label key={ev.id} className={`flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-sm ${checked ? 'bg-[#f1f7ff]' : 'hover:bg-[#fbfeff]'}`}>
                <input type="checkbox" className="mt-1" checked={checked} onChange={() => toggle(ev)} />
                <span className="min-w-0">
                  <span className="block truncate font-medium text-avcs-text">{codeLabel(ev.code)} · {ev.eventDate || '—'}{ev.dealNo ? ` · ${ev.dealNo}` : ''}</span>
                  <span className="block text-xs text-avcs-muted">{ev.dealName ? `${ev.dealName} · ` : ''}{ev.animalCount} animal{ev.animalCount === 1 ? '' : 's'}{ev.entityName ? ` · at ${ev.entityName}` : ''}</span>
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function CostLineItems({ dealHeads, lines, onChange }: {
  /** The deals selected at the top of the form, with their head. */
  dealHeads: DealHead[];
  lines: CostLine[];
  onChange: (lines: CostLine[]) => void;
}) {
  const [openLineId, setOpenLineId] = useState<string>(lines[0]?.id || '');
  const dealIds = useMemo(() => dealHeads.map((d) => d.dealId), [dealHeads]);
  const dealKey = dealIds.join(',');
  // Events already carrying a cost (a shared record names them) are not offered again.
  const [costedEventIds, setCostedEventIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    let cancelled = false;
    Promise.all(dealIds.map((id) => fetchSharedFinances({ dealId: id }).catch(() => [])))
      .then((groups) => {
        if (cancelled) return;
        const ids = new Set<string>();
        groups.flat().forEach((rec) => rec.basisEvents.forEach((ev) => ids.add(ev.id)));
        setCostedEventIds(ids);
      });
    return () => { cancelled = true; };
  }, [dealKey]); // eslint-disable-line
  const totalHead = dealHeads.reduce((s, d) => s + d.animals, 0);
  const patch = (id: string, p: Partial<CostLine>) => onChange(lines.map((l) => (l.id === id ? { ...l, ...p } : l)));
  const remove = (id: string) => onChange(lines.filter((l) => l.id !== id));
  const add = () => { const line = newCostLine(lines[lines.length - 1]?.eventKind || 'VACCINATION'); onChange([...lines, line]); setOpenLineId(line.id); };
  const total = costLinesTotal(lines);
  return (
    <div className="grid gap-3">
      {lines.length === 0 && <p className="text-sm text-avcs-muted">No cost lines yet — add the first one off the invoice.</p>}
      {lines.map((line, index) => {
        const picked = line.eventIds.map((id) => line.events[id]).filter(Boolean);
        const head = picked.length > 0 ? picked.reduce((s, ev) => s + ev.animalCount, 0) : totalHead;
        const split = costLineSplit(line, dealHeads);
        const open = openLineId === line.id;
        const complete = costLineIsComplete(line);
        return (
          <div key={line.id} className={`rounded-lg border bg-white p-3 ${complete ? 'border-[#b9e7c9]' : 'border-[#1e6bb824]'}`}>
            <div className="grid gap-2 md:grid-cols-[minmax(11rem,1fr)_2fr_minmax(9rem,0.8fr)_auto] md:items-end">
              <label className="grid gap-1 text-sm text-avcs-text">
                {index === 0 ? 'Event type' : <span className="sr-only">Event type</span>}
                <select className="rounded-lg border border-[#1e6bb833] bg-white px-3 py-2" value={line.eventKind} onChange={(e) => patch(line.id, { eventKind: e.target.value as EventKind, eventIds: [], events: {} })}>
                  {EVENT_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
              </label>
              <label className="grid gap-1 text-sm text-avcs-text">
                {index === 0 ? 'Line description' : <span className="sr-only">Line description</span>}
                <input className="rounded-lg border border-[#1e6bb833] px-3 py-2" placeholder="e.g. Duplocillin + Ecomectin, 8 Apr" value={line.description} onChange={(e) => patch(line.id, { description: e.target.value })} />
              </label>
              <label className="grid gap-1 text-sm text-avcs-text">
                {index === 0 ? 'Cost (R, ex VAT)' : <span className="sr-only">Cost</span>}
                <input type="number" min={0} step="0.01" className="rounded-lg border border-[#1e6bb833] px-3 py-2 tabular-nums" value={line.amount} onChange={(e) => patch(line.id, { amount: e.target.value })} />
              </label>
              <button type="button" className="mb-0.5 justify-self-end text-sm text-avcs-muted hover:text-[#6b1010]" aria-label="Remove line" onClick={() => remove(line.id)}>×</button>
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
              <button type="button" className="font-semibold text-[#1e6bb8] hover:underline" onClick={() => setOpenLineId(open ? '' : line.id)}>
                {picked.length === 0
                  ? `No events — divided across all ${head} animals in the selected deal${dealHeads.length === 1 ? '' : 's'}${Number(line.amount) > 0 && head > 0 ? ` · ${fmtR(Number(line.amount) / head)} per animal` : ''} · select events`
                  : `${picked.length} event${picked.length === 1 ? '' : 's'} · ${head} animals${Number(line.amount) > 0 && head > 0 ? ` · ${fmtR(Number(line.amount) / head)} per animal` : ''}`}{open ? ' ▴' : ' ▾'}
              </button>
              {split.length > 0 && Number(line.amount) > 0 && (
                <span className="flex flex-wrap gap-1">
                  {split.map((s) => <span key={s.dealNo} className="rounded-full border border-[#1e6bb824] bg-[#fbfeff] px-2 py-0.5 text-avcs-text"><span className="font-semibold">{s.dealNo}</span> {s.animals} head · {fmtR(s.amount)}</span>)}
                </span>
              )}
            </div>
            {open && <div className="mt-2"><LineEventPicker line={line} dealIds={dealIds} costedEventIds={costedEventIds} onChange={(p) => patch(line.id, p)} /></div>}
          </div>
        );
      })}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" className="btn" onClick={add}>+ Add line</button>
        <span className="text-sm text-avcs-text">Lines total <span className="font-semibold tabular-nums">{fmtR(total)}</span>{lines.some((l) => !costLineIsComplete(l)) && <span className="ml-2 text-xs text-[#7b4a18]">every line needs a description and a cost</span>}</span>
      </div>
    </div>
  );
}
